import React, { useEffect, useState } from 'react';
import SchoolLayout from '../../components/SchoolLayout';
import axios from 'axios';
import { User, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PendingMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => { fetchPendingMembers(); }, []);

  const fetchPendingMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/members/pending`);
      setMembers(response.data);
    } catch (error) {
      toast.error('Gagal memuatkan senarai permohonan');
    } finally { setLoading(false); }
  };

  const handleApprove = async (memberId) => {
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/members/${memberId}/status`, { status: 'Active', remarks: 'Diluluskan oleh perguruan' });
      toast.success('Ahli berjaya diluluskan');
      fetchPendingMembers();
    } catch { toast.error('Gagal meluluskan ahli'); } finally { setUpdating(false); }
  };

  const handleReject = async (memberId) => {
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/members/${memberId}/status`, { status: 'Inactive', remarks: 'Ditolak oleh perguruan' });
      toast.success('Permohonan telah ditolak');
      fetchPendingMembers();
    } catch { toast.error('Gagal menolak permohonan'); } finally { setUpdating(false); }
  };

  return (
    <SchoolLayout>
      <div className="space-y-5 max-w-5xl">

        {/* Header */}
        <div>
          <div className="text-xs font-bold text-[#D4AF37] uppercase tracking-widest mb-1">Portal Perguruan</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" data-testid="page-title">Permohonan Ahli</h1>
          <p className="text-sm text-[#777] mt-0.5">Semak dan luluskan permohonan ahli baru</p>
        </div>

        <div className="h-px bg-[#E5E5E5]" />

        {loading ? (
          <div className="text-center py-16 text-sm text-[#999] animate-pulse">Memuatkan permohonan...</div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 bg-white rounded border border-[#E5E5E5]">
            <User size={36} className="text-[#E5E5E5] mx-auto mb-3" />
            <p className="text-sm text-[#777]">Tiada permohonan ahli baru</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {members.map((member) => (
              <div
                key={member.id}
                className="bg-white rounded border-l-4 border-l-[#D4AF37] border border-[#E5E5E5] p-4 hover:shadow-sm transition-shadow"
                data-testid={`pending-card-${member.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-full bg-[#FFFBF0] flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="font-mono text-[10px] text-[#D71920] font-bold">{member.member_no}</p>
                      <span className="inline-block mt-0.5 px-2 py-0.5 rounded text-[10px] font-bold bg-[#FFFBF0] text-[#92600a] border border-[#D4AF37]/40">
                        Pending
                      </span>
                    </div>
                  </div>
                </div>

                <h3 className="font-bold text-[#0B0B0B] text-sm mb-3 leading-tight">{member.full_name}</h3>

                <div className="space-y-1.5 text-xs text-[#777] mb-4">
                  {member.ic_masked && <div className="flex justify-between"><span>IC:</span><span className="font-mono">{member.ic_masked}</span></div>}
                  {member.gender && <div className="flex justify-between"><span>Jantina:</span><span>{member.gender}</span></div>}
                  {member.phone && <div className="flex justify-between"><span>Telefon:</span><span>{member.phone}</span></div>}
                </div>

                <div className="flex gap-2 pt-3 border-t border-[#F7F7F7]">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded bg-[#16a34a] hover:bg-[#15803D] text-white text-xs font-semibold transition-colors disabled:opacity-50"
                        disabled={updating}
                      >
                        <CheckCircle size={13} /> Lulus
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Luluskan Ahli</AlertDialogTitle>
                        <AlertDialogDescription>
                          Luluskan <strong>{member.full_name}</strong> sebagai ahli aktif perguruan?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleApprove(member.id)} className="bg-[#16a34a] hover:bg-[#15803D]">
                          Ya, Luluskan
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 transition-colors disabled:opacity-50"
                        disabled={updating}
                      >
                        <XCircle size={13} /> Tolak
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Tolak Permohonan</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tolak permohonan keahlian <strong>{member.full_name}</strong>?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleReject(member.id)} className="bg-red-600 hover:bg-red-700">
                          Ya, Tolak
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center text-xs text-[#999]">
          {members.length} permohonan menunggu kelulusan
        </div>
      </div>
    </SchoolLayout>
  );
};

export default PendingMembers;
